"use client";

import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import If from "~/core/ui/If";

import { MODAL_ALLOWED_STATUS, MODAL_FORBIDDEN_ROUTES } from "../constants";
import { SubscriptionStatus } from "../enums";
import { useSubscriptionStore } from "../store";

import SubscriptionContent from "./Content";
import SubscriptionWrapper from "./Wrapper";

const SubscriptionModal = () => {
  const pathname = usePathname();
  const { subscription, isLoading } = useSubscriptionStore();

  const [isMounted, setIsMounted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const isForbiddenRoute =
      pathname === "/" ||
      MODAL_FORBIDDEN_ROUTES.some(route => pathname?.startsWith(route));

    const isAllowedStatus = MODAL_ALLOWED_STATUS.includes(
      subscription?.status as SubscriptionStatus,
    );

    const shouldShow = !isForbiddenRoute && isAllowedStatus && !isLoading;

    setShowModal(shouldShow);
  }, [pathname, subscription?.status, isLoading, isMounted]);

  if (!isMounted) return null;

  return (
    <SubscriptionWrapper>
      {createPortal(
        <AnimatePresence>
          <If condition={showModal}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            >
              <div className="relative z-10">
                <SubscriptionContent />
              </div>
            </motion.div>
          </If>
        </AnimatePresence>,
        document.body,
      )}
    </SubscriptionWrapper>
  );
};

export default SubscriptionModal;
